/**
 * Checkout cart inventory (IM-005 UI): resolve per-line stock using warehouse
 * `inventoryIds` from cookies after address / shipping method changes.
 */

import {useCallback, useMemo} from 'react';

import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react';

import {useCheckoutShopperInventoryIds} from '@amblique/king-living/app/hooks/use-checkout-shopper-inventory-ids';
import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import {
  collectBasketCatalogProductIds,
  groupComplexLineItems,
  resolveBasketRemovalGroup
} from '@amblique/king-living/app/utils/cart-utils';
import {getInventory, INVENTORY_STATUS} from '@amblique/king-living/app/utils/inventory-utils';

const INVENTORY_ORDER_ERROR =
  /inventory|out[\s-]?of[\s-]?stock|not\s+available|not\s+orderable|productitem.*unavailable|unavailable.*product/;

function productsByIdFromQueryResult(result) {
  return result?.data?.reduce((acc, item) => {
    acc[item.id] = item;

    return acc;
  }, {});
}

function isLineOutOfStock(line, productsByProductId, shopperInventoryIds) {
  const product = productsByProductId?.[line.productId];

  if (!product) {
    return false;
  }

  return getInventory(product, undefined, shopperInventoryIds).status === INVENTORY_STATUS.OUT_OF_STOCK;
}

/**
 * Evaluates checkout OOS state for visible basket rows (IM-005).
 *
 * Iterates `groupComplexLineItems(basket.productItems).visibleItems` only — hidden
 * recommendation / option child rows are not surfaced individually. For each visible
 * row, resolves the configuration group via `resolveBasketRemovalGroup`; when any line
 * in `removalOrder` is OOS (`ats <= 0` and not orderable for the warehouse context),
 * adds the parent visible `itemId` (`parent.itemId ?? visibleLine.itemId`) to the set.
 *
 * @param {Object|null|undefined} basket - Current shopper basket (`productItems` required)
 * @param {Object<string, Object>|null|undefined} productsByProductId - Products keyed by `productId` (from `getProducts` / `item.id`)
 * @param {string|undefined|null} shopperInventoryIds - IM-004 SCAPI `inventoryIds` (warehouse cookie)
 * @returns {{
 *   hasOutOfStockItems: boolean,
 *   outOfStockItemIds: Set<string>,
 * }}
 */
export function evaluateCheckoutCartOutOfStock(basket, productsByProductId, shopperInventoryIds) {
  const outOfStockItemIds = new Set();
  const productItems = basket?.productItems ?? [];

  if (!productsByProductId || !productItems.length) {
    return {hasOutOfStockItems: false, outOfStockItemIds};
  }

  groupComplexLineItems(productItems).visibleItems.forEach((visibleLine) => {
    const {removalOrder, parent} = resolveBasketRemovalGroup(visibleLine, productItems);
    const reportItemId = parent?.itemId ?? visibleLine.itemId;

    if (
      reportItemId &&
      removalOrder.some((line) => isLineOutOfStock(line, productsByProductId, shopperInventoryIds))
    ) {
      outOfStockItemIds.add(reportItemId);
    }
  });

  return {
    hasOutOfStockItems: outOfStockItemIds.size > 0,
    outOfStockItemIds,
  };
}

/**
 * Whether a `createOrder` (or similar) failure looks like an inventory / unavailable-product fault.
 *
 * @param {unknown} error - SDK / fetch error object
 * @returns {boolean}
 */
export function isCheckoutInventoryOrderError(error) {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error;
  const responseData =
    typeof err.response?.data === 'string' ?
      err.response.data :
      JSON.stringify(err.response?.data ?? '');

  const text = [
    err.message,
    err.statusMessage,
    err.type,
    err.fault?.message,
    err.fault?.type,
    responseData
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return INVENTORY_ORDER_ERROR.test(text);
}

/**
 * Checkout inventory source of truth: loads basket products once, evaluates warehouse OOS,
 * and exposes data for payment-amount gating (KINDW-195) and place-order validation (IM-005).
 *
 * @param {Object|null|undefined} basket - Current shopper basket
 * @param {{ enabled?: boolean }} [options] - When `false`, skips the `useProducts` query (e.g. cart flyout closed)
 * @returns {{
 *   hasOutOfStockItems: boolean,
 *   outOfStockItemIds: Set<string>,
 *   isInventoryLoading: boolean,
 *   productsByProductId: Object<string, Object>|undefined,
 *   shopperInventoryIds: string|undefined,
 *   validateInventoryBeforePlaceOrder: () => Promise<{
 *     blocked: boolean,
 *     hasOutOfStockItems: boolean,
 *     outOfStockItemIds: Set<string>,
 *   }>,
 * }}
 */
export function useCheckoutCartInventory(basket, options = {}) {
  const {enabled = true} = options;
  const {shopperInventoryIds, isInventoryIdsResolving} = useCheckoutShopperInventoryIds();
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();

  // KL-1028 — single product source for checkout. Includes master ids (via
  // `collectBasketCatalogProductIds`) so the order-summary line items can reuse
  // this map instead of issuing a second, overlapping `useProducts` (Finding 1).
  const productIds = useMemo(
    () => collectBasketCatalogProductIds(basket?.productItems ?? []).join(','),
    [basket?.productItems]
  );

  const {
    data: productsByProductId,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
    refetch: refetchProducts,
  } = useProducts(
    {
      parameters: {ids: productIds, allImages: true, perPricebook: true},
    },
    {
      enabled: enabled && Boolean(productIds) && !isInventoryIdsResolving,
      select: productsByIdFromQueryResult,
    },
    {inventoryIds: shopperInventoryIds, mode: 'checkout'}
  );

  const inventoryState = useMemo(
    () => evaluateCheckoutCartOutOfStock(basket, productsByProductId, shopperInventoryIds),
    [basket, productsByProductId, shopperInventoryIds]
  );

  /** Refetch products, then re-run {@link evaluateCheckoutCartOutOfStock} with fresh data. */
  const validateInventoryBeforePlaceOrder = useCallback(async () => {
    if (!productIds || !basket?.productItems?.length) {
      return {blocked: false, hasOutOfStockItems: false, outOfStockItemIds: new Set()};
    }

    const {data: freshProducts} = await refetchProducts();

    return {
      blocked: false,
      ...evaluateCheckoutCartOutOfStock(
        basket,
        freshProducts ?? productsByProductId,
        shopperInventoryIds
      ),
    };
  }, [basket, productIds, productsByProductId, refetchProducts, shopperInventoryIds]);

  // KINDW — imperative product loader for the pre-place-order guard.
  //
  // The `useProducts` query above is keyed off the *current* basket's
  // `productItems`. When a cross-tab edit adds items, the guard refetches the
  // basket and needs to judge deposit eligibility (`isPaymentAmountSectionVisible`)
  // against the NEW line-up immediately — but `productsByProductId` won't
  // include the added items until the query key updates on a later render.
  // Waiting for that render is what caused the guard to block twice (once for
  // the content change, then again once the added item's data loaded and
  // revealed deposit was no longer valid). This fetches any missing ids on
  // demand and returns a merged map so the decision is correct on the first
  // attempt. Non-fatal on failure — falls back to whatever data we already have.
  const loadProductsForIds = useCallback(
    async (ids) => {
      const uniqueIds = [...new Set((ids ?? []).filter(Boolean))];
      const missing = uniqueIds.filter((id) => !productsByProductId?.[id]);

      if (!missing.length) return productsByProductId;

      try {
        const token = await getTokenWhenReady();
        const res = await api.shopperProducts.getProducts({
          headers: {Authorization: `Bearer ${token}`},
          parameters: {
            ids: missing.join(','),
            allImages: true,
            perPricebook: true,
            ...(shopperInventoryIds ? {inventoryIds: shopperInventoryIds} : {}),
          },
        });

        return {...(productsByProductId ?? {}), ...(productsByIdFromQueryResult(res) ?? {})};
      } catch {
        return productsByProductId;
      }
    },
    [api, getTokenWhenReady, productsByProductId, shopperInventoryIds]
  );

  // `productsByProductId` and `shopperInventoryIds` are exposed for
  // downstream consumers (Payment Amount visibility, deposit eligibility
  // splits) so they re-use this query's React Query cache instead of
  // issuing a parallel `useProducts` for the same productIds.
  return {
    ...inventoryState,
    isInventoryLoading:
      Boolean(productIds) &&
      (isProductsLoading || isProductsFetching || isInventoryIdsResolving),
    validateInventoryBeforePlaceOrder,
    loadProductsForIds,
    productsByProductId,
    shopperInventoryIds,
  };
}

export default useCheckoutCartInventory;
